import React, { useMemo, useEffect, useState } from 'react'
import { objectOf, bool, any, number, shape, func, oneOfType, arrayOf, node, string, object } from 'prop-types'
import { Grid, styled, css, Typography, useMediaQuery, useTheme } from '@mui/material'
import { isEmpty } from 'lodash'
import _capitalize from 'lodash/capitalize'
import { getRegionZone } from '@helpers/geo-location'
import { setWarrantyEnabled, getCart, getCartTotal, getLineItemQuantity } from '@helpers/cart'
import { decodeTrademarkEntity, titleCase } from '@helpers/string-helper'
import {
  productPrice,
  isProductStrikeThrough,
  getProductSaleFlagType,
  SaleFlagType,
  earliestStockDate,
  getImagesForProductTile,
  getProductDisplayImage,
} from '@helpers/product'
import { announce } from '@helpers/aria-announce'
import { useSelector } from 'react-redux'
import { useProduct } from '../hooks'
import ProductPrice from '../../../@rtg2022/components/atoms/ProductPrice/ProductTilePrice'
import ProductWarranty from './product-warranty'
import AddtoCartModalBonusBuy from './add-to-cart-modal-bonus-buy'

const AddToCartModalProductsStyled = styled(Grid)(
  ({ theme }) => css`
    margin-top: 0;
    margin-left: -16px;
    padding-top: 15px;
    &__Image {
      min-height: 225px;
    }
    .AddToCartModalProduct {
      &__Title {
        font-weight: 400;
        font-size: 12px;
        line-height: normal;
      }
      &__Color {
        margin-bottom: 18px;
        align-items: center;
        p {
          color: ${theme.palette.text.secondary};
          margin-left: 10px;
        }
      }
      &__Stock {
        margin-bottom: 5px;
        font-weight: 500;
      }
      &__Price {
        margin: 2px 0;
        span {
          font-size: 16px;
          font-weight: 600;
        }
      }
      ${theme.breakpoints.down('sm')} {
        &__Image {
          min-height: auto;
          max-width: 100px;
        }
        &__Actions {
          z-index: 2;
          width: 100%;
          left: 0;
          padding-bottom: 0;
          background: white;
        }
      }
      ${theme.breakpoints.up('sm')} {
        &__Details__Column {
          position: relative;
        }
        &__Actions {
          /* position: absolute; */
          left: 0;
          right: 0;
          bottom: 0;
        }
        &__Image {
          padding-right: 1em;
          img {
            // make sure different aspect ratios are not too big for the modal
            height: 50px;
            width: 100px;
            object-fit: contain;
            background-color: #dddbdd;
            &::after {
              position: absolute;
              top: 0;
              right: 0;
              bottom: 0;
              left: 0;
              transform: translateX(-100%);
              background-image: linear-gradient(
                90deg,
                rgba(255, 255, 255, 0) 0,
                rgba(255, 255, 255, 0.2) 20%,
                rgba(255, 255, 255, 0.5) 60%,
                rgba(255, 255, 255, 0)
              );
              animation: gradient 2.5s ease infinite;
              content: '';
            }
          }
          @keyframes gradient {
            100% {
              transform: translateX(100%);
            }
            max-height: 75px;
            max-width: 100px;
          }
        }
      }
      ${theme.breakpoints.down('xs')} {
        &__Image {
          img {
            max-height: 75px;
          }
        }
        &__TitlePrice {
          margin-left: 15px;
        }
      }
    }
    .atc-product-image {
      pointer-events: none;
    }
  `,
)

const StyledRule = styled('hr')(
  () => css`
    margin: 15px 0 0 0 !important;
  `,
)

const ProductContainer = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  padding: '0 !important',
  marginBottom: '10px',
  paddingLeft: '16px !important',
}))

const StyledActionsGrid = styled(Grid)(
  ({ hasBonusBuy }) => css`
    display: flex;
    justify-content: ${hasBonusBuy ? 'end' : 'center'};
    ${!hasBonusBuy ? 'padding-top: 0 !important;' : ''};
    padding-bottom: 20px !important;
    flex-direction: column;
  `,
)

const AddToCartModalProducts = ({
  product,
  promoQualifierQuantity,
  foundationSelection,
  warrantyInfo: { isWarrantyEnabled, warrantyData },
  reachedCartLimit,
  reachedGiftCardMaxPrice,
  showWarrantyModal,
  toggleWarrantyEnabled,
  includesRequiredAddon,
  isStateEligible,
  className,
  style,
  children,
  promoAddedToCart,
  selectedPromo,
}) => {
  const { isThankyouDiscount } = useProduct({ product })
  const { distribution_index, region, zone } = getRegionZone()
  const [isLoadingImage, setIsLoadingImage] = useState(true)
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'))
  const cartState = useSelector(state => state.cart?.cart)
  const cartTotal = getCartTotal(cartState)
  const cartQuantity = useSelector(state => getLineItemQuantity(state.order).cart)
  const toggleWarranty = value => {
    toggleWarrantyEnabled(value)
    if (includesRequiredAddon) {
      setWarrantyEnabled(product.sku, value)
    } else {
      const cart = getCart()
      setWarrantyEnabled(
        product.sku,
        value,
        cart?.cartItems?.findIndex(item => item.sku === product.sku),
      )
    }
  }

  const productTitle = useMemo(
    () => decodeTrademarkEntity(product?.displayNamePDP || product?.title || '', [' ', ',', '-']),
    [product],
  )

  const pricing = useMemo(() => {
    if (!product) return {}
    const isCloseout = isProductStrikeThrough(product)
    const earliestStockDateValue = earliestStockDate(product, distribution_index)
    const saleFlagTypeLabel = getProductSaleFlagType({ product, availabilityDate: earliestStockDateValue })
    const productIsOnSale = saleFlagTypeLabel !== SaleFlagType.InStock && saleFlagTypeLabel !== SaleFlagType.None
    return {
      price: productPrice(product),
      strikeThrough: isCloseout,
      strikeThroughPrice: productPrice(product, isCloseout),
      sale: productIsOnSale,
      highlightPrice: saleFlagTypeLabel?.toLowerCase() === 'last chance' || isThankyouDiscount,
    }
  }, [isThankyouDiscount, product, distribution_index])

  const productImage = useMemo(() => {
    if (product?.primary_image?.startsWith('/static/')) {
      return `${product.primary_image}?h=75&w=100`
    }
    return product ? `${getProductDisplayImage(product) || getImagesForProductTile(product)}&h=285` : null
  }, [product])

  const onImageLoad = () => {
    setIsLoadingImage(false)
  }

  useEffect(() => {
    if (!productImage) return
    // eslint-disable-next-line no-undef
    const img = new Image()
    img.onload = onImageLoad
    img.src = productImage
  }, [productImage])

  const hasBonusBuy = useMemo(() => {
    if (product && product.promotions) {
      const promotion = product.promotions[`${region}_${zone}`] || product.promotions[`${region}_0`]
      return promotion && promotion.promo_type !== 'BUY X AMOUNT GET Y DISCOUNT' && promoAddedToCart
    }
    return false
  }, [product, promoAddedToCart, region, zone])

  let promotions
  if (product && product.promotions) {
    promotions = product.promotions[`${region}_${zone}`] || product.promotions[`${region}_0`]
  }

  useEffect(() => {
    announce(`${productTitle} added to cart successfully. ${cartQuantity} cart Item(s). Cart total: $${cartTotal}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AddToCartModalProductsStyled
      container
      spacing={2}
      key={`add-to-cart-modal-product_${product.sku}`}
      className={className}
      style={style}
    >
      <ProductContainer item xs={12} sm={hasBonusBuy ? 12 : 8}>
        <div className="AddToCartModalProduct__Image">
          <img src={`${productImage}`} alt={product.title} />
        </div>
        <div className="AddToCartModalProduct__TitlePrice">
          <Typography
            variant="h3"
            className="AddToCartModalProduct__Title"
            dangerouslySetInnerHTML={{ __html: productTitle }}
          />
          <div className="AddToCartModalProduct__Price">
            <ProductPrice
              price={pricing.price}
              strikeThrough={pricing.strikeThrough}
              strikeThroughPrice={pricing.strikeThroughPrice}
              sale={pricing.sale}
              highlightPrice={pricing.highlightPrice}
              fontSize="21px"
            />
          </div>
        </div>
      </ProductContainer>
      {hasBonusBuy && (
        // <ProductContainer container>
        <AddtoCartModalBonusBuy
          product={product}
          promo={selectedPromo}
          promoDescription={promotions.offer_description}
          qualifierQuantity={promotions.qualifier_value}
          targetQuantity={promotions.target_value}
          promoType={promotions.offer_template}
        />
        // </ProductContainer>
      )}

      <StyledActionsGrid item xs={12} sm={4} hasBonusBuy={hasBonusBuy}>
        {children && children}
      </StyledActionsGrid>
      {warrantyData && <StyledRule />}
      <Grid item xs={12} className="AddToCartModalProduct__Details__Column">
        {/* TODO: Check business if these conditions still applies ? */}
        <p>{promoQualifierQuantity && promoQualifierQuantity > 1 && ` (${promoQualifierQuantity})`}</p>
        {!isEmpty(foundationSelection) && product?.size && <p>{`Size: ${titleCase(product.size)}`}</p>}
        {!isEmpty(foundationSelection) && <p>{`Foundation: ${titleCase(foundationSelection.title)}`}</p>}

        {!reachedCartLimit && !reachedGiftCardMaxPrice && warrantyData && warrantyData.price > 0 && isStateEligible && (
          <div className="AddToCartModalProduct__Warranty">
            <ProductWarranty
              isMobile={isMobile}
              product={product}
              showWarrantyModal={showWarrantyModal}
              price={warrantyData.price}
              isWarrantyEnabled={isWarrantyEnabled}
              onToggleWarranty={toggleWarranty}
            />
          </div>
        )}
      </Grid>
    </AddToCartModalProductsStyled>
  )
}

AddToCartModalProducts.propTypes = {
  foundationSelection: objectOf(any),
  promoQualifierQuantity: number,
  product: objectOf(any),
  warrantyInfo: shape({
    warrantyPrice: number,
    warrantyEnabled: bool,
    warrantyData: objectOf(any),
    isWarrantyEnabled: bool,
  }),
  reachedCartLimit: bool,
  reachedGiftCardMaxPrice: bool,
  includesRequiredAddon: bool,
  toggleWarrantyEnabled: func,
  showWarrantyModal: func,
  isStateEligible: bool,
  children: oneOfType([arrayOf(node), node]),
  style: object,
  className: string,
  promoAddedToCart: bool,
  selectedPromo: object,
}

export default AddToCartModalProducts
